import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
 
const WORDS = [
  'happy', 'swift', 'brave', 'quiet', 'smart',
  'noble', 'eager', 'kind', 'wise', 'bold',
  'calm', 'proud', 'free', 'pure', 'warm',
  'light', 'cool', 'fair', 'true', 'keen'
]

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function generateRandomUsername(): string {
  const word = WORDS[Math.floor(Math.random() * WORDS.length)]
  const numbers = Math.floor(10000 + Math.random() * 90000)
  return `${word}${numbers}`
}

// New utility functions for flight card
export const formatCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

/** Format API meal value (e.g. no_meal, half_board) for display */
export function formatMealPlan(value: string): string {
  return value.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** True if meal plan should be shown in UI (hide no_meal / no-meal to avoid redundant "Meal: No Meal") */
export function isDisplayableMealPlan(value: string | undefined): boolean {
  if (!value || !String(value).trim()) return false;
  const normalized = String(value).replace(/[_-]/g, ' ').toLowerCase().trim();
  return normalized !== 'no meal';
}

export const formatTime = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return String(dateString);
  }
};

export const formatDate = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    return String(dateString);
  }
};

export const isSameDay = (date1: string | Date, date2: string | Date): boolean => {
  try {
    const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
    const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
    
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  } catch (e) {
    return true;
  }
};

export const calculateLayoverDuration = (arrivalTime: string | Date, departureTime: string | Date): string => {
  try {
    const arrival = typeof arrivalTime === 'string' ? new Date(arrivalTime) : arrivalTime;
    const departure = typeof departureTime === 'string' ? new Date(departureTime) : departureTime;
    
    const durationMs = departure.getTime() - arrival.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  } catch (e) {
    return 'Unknown';
  }
};

export const isLongLayover = (arrivalTime: string | Date, departureTime: string | Date): boolean => {
  try {
    const arrival = typeof arrivalTime === 'string' ? new Date(arrivalTime) : arrivalTime;
    const departure = typeof departureTime === 'string' ? new Date(departureTime) : departureTime;
    
    const durationMs = departure.getTime() - arrival.getTime();
    const hours = durationMs / (1000 * 60 * 60);
    
    return hours >= 3;
  } catch (e) {
    return false;
  }
};

export const isOvernightFlight = (departureTime: string | Date, arrivalTime: string | Date): boolean => {
  try {
    const departure = typeof departureTime === 'string' ? new Date(departureTime) : departureTime;
    const arrival = typeof arrivalTime === 'string' ? new Date(arrivalTime) : arrivalTime;
    
    const departureHour = departure.getHours();
    const arrivalHour = arrival.getHours();
    
    // Consider overnight if flight departs after 10 PM or arrives before 6 AM
    // and it's not a same-day arrival
    const isLateNightDeparture = departureHour >= 22;
    const isEarlyMorningArrival = arrivalHour <= 6;
    const crossesDay = !isSameDay(departure, arrival);
    
    return (isLateNightDeparture || isEarlyMorningArrival) && crossesDay;
  } catch (e) {
    return false;
  }
};

export const parseDuration = (duration: string): number => {
  // Handle ISO 8601 duration format (e.g., "P1DT1H3M")
  if (duration.startsWith('p') || duration.startsWith('P')) {
    // ISO 8601 format: P[n]D[n]H[n]M[n]S
    const isoMatch = duration.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/i);
    if (isoMatch) {
      const days = parseInt(isoMatch[1] || '0', 10);
      const hours = parseInt(isoMatch[2] || '0', 10);
      const minutes = parseInt(isoMatch[3] || '0', 10);
      // Seconds are ignored for calculation purposes
      return (days * 24 * 60) + (hours * 60) + minutes;
    }
  }
  
  // Handle standard format (e.g., "1h 30m" or "90m")
  const hourMatch = duration.match(/(\d+)h/);
  const minuteMatch = duration.match(/(\d+)m/);
  
  const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
  
  return hours * 60 + minutes;
};

export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}m`;
  }
};

/**
 * Formats a duration string (handles ISO 8601 format like "p1dt1h3m")
 * @param duration - Duration string in various formats
 * @returns Formatted duration string (e.g., "1d 1h 3m" or "25h 3m")
 */
export const formatDurationString = (duration: string): string => {
  if (!duration) return '';
  
  // Handle ISO 8601 duration format (e.g., "P1DT1H3M" or "p1dt1h3m")
  if (duration.startsWith('p') || duration.startsWith('P')) {
    // ISO 8601 format: P[n]D[n]H[n]M[n]S
    // Example: P1DT1H3M = 1 day, 1 hour, 3 minutes
    const isoMatch = duration.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/i);
    if (isoMatch) {
      const days = parseInt(isoMatch[1] || '0', 10);
      const hours = parseInt(isoMatch[2] || '0', 10);
      const minutes = parseInt(isoMatch[3] || '0', 10);
      // Seconds are ignored for display purposes
      
      const parts: string[] = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      
      return parts.join(' ') || '0m';
    }
  }
  
  // If already in a readable format, return as-is
  return duration;
};